package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDate
import kotlin.math.roundToInt

/**
 * SH-226: the weather at a workplace, fetched by the server (functions/weather.js, docs/weather.md)
 * when the workplace has it on (WorkplaceSettings.weatherEnabled) and a location. Never typed in,
 * never fetched by the app: it reads `workplaces/{id}/weather/{date}`.
 */
enum class WeatherCondition {
    SUNNY, PARTLY_CLOUDY, CLOUDY, RAIN, SNOW, STORM;

    /** Rain, snow or a storm: the days that change how busy a place is. */
    val isWet: Boolean get() = this == RAIN || this == SNOW || this == STORM

    companion object {
        /** Tolerant lookup: unknown or missing means null (shown as no icon, the temperature alone). */
        fun fromName(name: String?): WeatherCondition? = entries.firstOrNull { it.name == name }
    }
}

/** Where a day's weather comes from; each needs its own attribution (docs/weather.md). */
enum class WeatherSource {
    /** MET Norway Locationforecast 2.0, CC BY 4.0. */
    MET_NORWAY,
    /** IMGW-PIB synop observations (Poland), the nearest station. */
    IMGW;

    companion object {
        fun fromName(name: String?): WeatherSource? = entries.firstOrNull { it.name == name }
    }
}

/** One day's summary: the forecast or what was measured. Temperatures in °C, rain in mm, wind in m/s. */
data class WeatherSummary(
    val minC: Double,
    val maxC: Double,
    val condition: WeatherCondition?,
    val precipitationMm: Double? = null,
    /** 0–100, forecasts only, when the source gives it. */
    val precipitationProbability: Int? = null,
    val windMs: Double? = null,
    val source: WeatherSource? = null,
    /** Observations: the station's name and how far it is, for the "measured at" line. */
    val stationName: String? = null,
    val distanceKm: Double? = null
) {
    /** The day's high, rounded, for the short label ("18°"). */
    val maxRounded: Int get() = maxC.roundToInt()
    val minRounded: Int get() = minC.roundToInt()
}

/**
 * A workplace's weather for one date: the forecast (kept after the day, as the fallback) and,
 * from the next morning, what was measured.
 */
data class DayWeather(
    val date: LocalDate,
    val forecast: WeatherSummary? = null,
    val observed: WeatherSummary? = null
) {
    /** What to show: what was measured when there is a measurement, otherwise the forecast. */
    val shown: WeatherSummary? get() = observed ?: forecast

    /** Whether [shown] is a measurement (false: a forecast, labelled as such). */
    val isObserved: Boolean get() = observed != null

    /**
     * The sources behind [shown], for the attribution line: a measured day's icon on a dry day
     * comes from that day's forecast (functions/weather.js), so both are credited then.
     */
    val sources: Set<WeatherSource>
        get() = if (shown == null) emptySet() else setOfNotNull(observed?.source, forecast?.source)

    companion object {
        /** The sources of everything shown on a screen, for one attribution line. */
        fun sourcesOf(days: Iterable<DayWeather?>): Set<WeatherSource> =
            days.flatMapTo(linkedSetOf()) { it?.sources.orEmpty() }
    }
}
